import { useSearchParams } from 'react-router-dom'
import BackButton from '../ui-components/BackButton'
import RenderHTML from '../ui-components/RenderHTML'
import { getTopPadding } from '../../utils/sessionStorage'
import { goBack, goToPortfolioDashboard, trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import { useEliteBuyCtaExtras } from './EliteBuyCtaExtrasContext'

type ElitePageBackBarProps = {
  isEliteButtonVisible: boolean
}

export function ElitePageBackBar({ isEliteButtonVisible }: ElitePageBackBarProps) {
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')
  // Handlers live inside the component so the back/close funnel events carry the
  // screen's buy-CTA extras (amount + plan ids) from the provider.
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()

  const handleBack = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ...eliteBuyCtaExtras,
        eventId: 'webview_back',
        screenType: window?.location?.href?.includes('dashboard') ? 'MF_Elite_Dashboard' : 'MF_Elite_Landing'
      }
    })
    goToPortfolioDashboard()
  }

  const handleClose = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ...eliteBuyCtaExtras,
        eventId: 'webview_close',
        screenType: window?.location?.href?.includes('dashboard') ? 'MF_Elite_Dashboard' : 'MF_Elite_Landing'
      }
    })
    goBack()
  }

  if (from === 'HOME') {
    return null
  }

  const fill = isEliteButtonVisible ? '#000000' : '#FFFFFF'
  const backgroundClassName = isEliteButtonVisible ? 'bg-[#EBF2F2]' : 'bg-[#1C1D1E]'

  return (
    <div
      style={{ top: `${getTopPadding()}px` }}
      className={`flex absolute left-3 z-10 w-12 h-12 min-h-[24px] min-w-[24px] items-center justify-center rounded-[50%] animate-fade-150 ${backgroundClassName}`}
    >
      {from === 'APP_STARTUP' ? (
        <button
          type='button'
          aria-label='Close'
          onClick={handleClose}
          className='flex min-h-[24px] min-w-[24px] items-center justify-center border-none bg-transparent p-0 cursor-pointer'
        >
          <span aria-hidden='true'>
            <RenderHTML innerHTMLText={getCloseIcon(fill)} />
          </span>
        </button>
      ) : (
        <BackButton fill={fill} size={24} onClick={handleBack} />
      )}
    </div>
  )
}

export function getCloseIcon(fill: string) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M8.9804 7.46941C8.91521 7.40438 8.86349 7.32714 8.8282 7.2421C8.79291 7.15705 8.77475 7.06588 8.77475 6.97381C8.77475 6.88173 8.79291 6.79056 8.8282 6.70552C8.86349 6.62048 8.91521 6.54323 8.9804 6.47821L13.6928 1.76581C13.8783 1.56676 13.9792 1.3035 13.9744 1.03148C13.9696 0.759455 13.8595 0.499916 13.6671 0.307538C13.4747 0.11516 13.2152 0.00496309 12.9431 0.000163586C12.6711 -0.00463592 12.4078 0.0963366 12.2088 0.281809L7.4964 4.99421C7.43138 5.0594 7.35413 5.11112 7.26909 5.14641C7.18405 5.1817 7.09288 5.19986 7.0008 5.19986C6.90873 5.19986 6.81756 5.1817 6.73251 5.14641C6.64747 5.11112 6.57023 5.0594 6.5052 4.99421L1.7928 0.281809C1.59376 0.0963366 1.33049 -0.00463592 1.05847 0.000163586C0.786448 0.00496309 0.526909 0.11516 0.33453 0.307538C0.142152 0.499916 0.0319555 0.759455 0.027156 1.03148C0.0223565 1.3035 0.123329 1.56676 0.308801 1.76581L5.0212 6.47821C5.08639 6.54323 5.13811 6.62048 5.1734 6.70552C5.20869 6.79056 5.22685 6.88173 5.22685 6.97381C5.22685 7.06588 5.20869 7.15705 5.1734 7.2421C5.13811 7.32714 5.08639 7.40438 5.0212 7.46941L0.308801 12.1818C0.20564 12.2779 0.122896 12.3939 0.0655076 12.5227C0.00811884 12.6515 -0.0227397 12.7905 -0.0252272 12.9315C-0.0277147 13.0725 -0.00178019 13.2125 0.0510292 13.3432C0.103839 13.474 0.182441 13.5928 0.282147 13.6925C0.381854 13.7922 0.500621 13.8708 0.631364 13.9236C0.762108 13.9764 0.902149 14.0023 1.04313 13.9998C1.18412 13.9973 1.32316 13.9665 1.45195 13.9091C1.58075 13.8517 1.69667 13.769 1.7928 13.6658L6.5052 8.95341C6.57023 8.88822 6.64747 8.8365 6.73251 8.80121C6.81756 8.76592 6.90873 8.74776 7.0008 8.74776C7.09288 8.74776 7.18405 8.76592 7.26909 8.80121C7.35413 8.8365 7.43138 8.88822 7.4964 8.95341L12.2088 13.6658C12.4078 13.8513 12.6711 13.9523 12.9431 13.9475C13.2152 13.9427 13.4747 13.8325 13.6671 13.6401C13.8595 13.4477 13.9696 13.1882 13.9744 12.9161C13.9792 12.6441 13.8783 12.3809 13.6928 12.1818L8.9804 7.46941Z" fill="${fill}"/>
</svg>`
}
